import {
  memo,
  ReactNode,
  useEffect,
  useRef,
  useState,
  useCallback,
  CSSProperties,
} from 'react';
import { createPortal } from 'react-dom';
import { FocusOn } from 'react-focus-on';
import styles from './styles.module.scss';

export interface IStyleOverride {
  modal?: CSSProperties;
  container?: CSSProperties;
}

export const Modal = memo(
  ({
    onClose,
    children,
    styleOverride = {},
  }: {
    onClose?: () => void;
    children: ReactNode;
    styleOverride?: IStyleOverride;
  }) => {
    const domElement = document.getElementById('root');
    const modalRef = useRef<HTMLDivElement>(null);

    return (
      domElement &&
      createPortal(
        <FocusOn
          shards={[modalRef]}
          onClickOutside={onClose}
          onEscapeKey={onClose}
          scrollLock={false}
        >
          <div
            className={styles.useModal}
            style={styleOverride?.modal}
            aria-modal="true"
            role="dialog"
            aria-label="Modal Dialog"
          >
            <div
              className={styles.card}
              style={styleOverride?.container}
              ref={modalRef}
            >
              {children}
            </div>
          </div>
        </FocusOn>,
        domElement
      )
    );
  }
);

export const useModal = (
  {
    styleOverride = {},
  }: {
    styleOverride?: IStyleOverride;
  } = { styleOverride: {} }
) => {
  const [isModalVisible, setModalVisible] = useState(false);
  const bodyOverflowRef = useRef<string | null>(null);
  const bodyPaddingRightRef = useRef<string | null>(null);

  const showModal = () => setModalVisible(true);
  const hideModal = () => setModalVisible(false);

  const handleEsc = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') hideModal();
    },
    [hideModal]
  );

  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      const modal = document.querySelector(`.${styles.card}`) as HTMLDivElement;
      if (modal && !modal.contains(event.target as Node)) {
        hideModal();
      }
    },
    [hideModal]
  );

  // Scroll lock effect
  useEffect(() => {
    if (isModalVisible) {
      const { body } = document;

      // Save current values
      bodyOverflowRef.current = body.style.overflow;
      bodyPaddingRightRef.current = body.style.paddingRight;

      // Calculate scrollbar width before hiding overflow
      const scrollbarWidth =
        window.innerWidth - document.documentElement.clientWidth;

      // Set overflow hidden and padding to prevent layout shift
      body.style.overflow = 'hidden';
      if (scrollbarWidth > 0) {
        body.style.paddingRight = `${scrollbarWidth}px`;
      }

      window.addEventListener('keydown', handleEsc);
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      if (isModalVisible) {
        const { body } = document;

        // Restore original values
        if (bodyOverflowRef.current !== null) {
          body.style.overflow = bodyOverflowRef.current;
        } else {
          body.style.overflow = '';
        }

        if (bodyPaddingRightRef.current !== null) {
          body.style.paddingRight = bodyPaddingRightRef.current;
        } else {
          body.style.paddingRight = '';
        }

        window.removeEventListener('keydown', handleEsc);
        document.removeEventListener('mousedown', handleClickOutside);
      }
    };
  }, [isModalVisible, handleEsc, handleClickOutside]);

  const RenderModal = ({ children }: { children: ReactNode }) =>
    isModalVisible && (
      <Modal styleOverride={styleOverride} onClose={hideModal}>
        {children}
      </Modal>
    );

  return {
    isModalVisible,
    showModal,
    hideModal,
    RenderModal,
  };
};
